import type {
  QuestionnaireItem as FhirQuestionnaireItem,
  Questionnaire,
  QuestionnaireItemEnableWhen,
} from "@medplum/fhirtypes";
import { catalogModelKindOf, PERFORMANCE_MEASURE_DEFINITION_KIND } from "@repo/api";
import type { QuestionnaireFactCondition, QuestionnaireScoreCalculation } from "@repo/domain";
import { highWaterMark, implicitTotalScore, isMemberId, isQuestionnaireScoreCalculation } from "@repo/domain";
import {
  extensionUrl,
  MAX_VALUE_URL,
  MIN_VALUE_URL,
  ORDINAL_VALUE_EXTENSION_URL,
  QUESTIONNAIRE_ITEM_CONTROL_SYSTEM,
  QUESTIONNAIRE_ITEM_CONTROL_URL,
  SLIDER_STEP_VALUE_URL as STEP_URL,
  UCUM_SYSTEM,
  QUESTIONNAIRE_UNIT_URL as UNIT_URL,
} from "@repo/fhir-systems";
import { factConditionExtensions, withFactConditions } from "./patient-fact-condition-storage";

/**
 * Where a `Questionnaire` row keeps a questionnaire definition, for the read
 * side (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * `Questionnaire` STORES THREE MODELS — QuestionnaireDefinition, ExamDefinition
 * and FindingDefinition — and `meta.profile` is what says which. Every read
 * here goes through `assertQuestionnaireDefinitionRow` first, because without
 * it an exam row decodes as a questionnaire: no error, just a definition built
 * from another model's row.
 *
 * A row missing something the model requires THROWS rather than reading as
 * `""` or `NaN`: a substituted value is indistinguishable from a real one, so
 * every broken row would share a `""` lineage and a section with no label
 * would render as a blank heading nobody could trace. `deriveForSync` in
 * `./base.ts` catches this, logs the resource id and drops that one row, so
 * loud here costs one row rather than the collection. The section tree is read
 * at sync time for exactly that reason — the projection runs inside a live
 * query, where a throw blanks the page instead of dropping a row.
 *
 * The encoders are the read-side twins used by the optimistic writer only: an
 * optimistic row is read back through this same reader, so it has to be
 * written in the same storage shape or the authoritative row reads as a
 * flicker when it lands.
 */

export const QUESTIONNAIRE_DEFINITION_PROFILE = extensionUrl("questionnaire-definition");
export const QUESTIONNAIRE_SCORES_URL = extensionUrl("questionnaire-scores");
export const QUESTIONNAIRE_TOTAL_BANDS_URL = extensionUrl("questionnaire-total-bands");
export const QUESTIONNAIRE_MEMBER_MARKS_URL = extensionUrl("questionnaire-member-marks");
export const QUESTIONNAIRE_MIN_OCCURRENCES_URL = extensionUrl("questionnaire-min-occurrences");
const TEXT_FORMAT_URL = extensionUrl("questionnaire-text-format");
const OPTION_DETAIL_URL = extensionUrl("questionnaire-option-detail");
const ASK_YES_NO_FIRST_URL = extensionUrl("questionnaire-ask-yes-no-first");

export type QuestionnaireDefinitionAvailability = "Active" | "Inactive";

export type QuestionnaireConditionValue =
  | { kind: "Boolean"; value: boolean }
  | { kind: "Integer"; value: number }
  | { kind: "Quantity"; value: number }
  | { kind: "Text"; value: string }
  | { kind: "Date"; value: string }
  | { kind: "Choice"; optionId: string };

export type EnablementCondition = QuestionnaireCondition | QuestionnaireFactCondition;

export interface QuestionnaireCondition {
  question: string;
  operator: "=" | "!=" | ">" | "<" | ">=" | "<=" | "exists";
  value: QuestionnaireConditionValue;
}

export type QuestionnaireAnswerValue =
  | { kind: "Quantity"; unit?: { code: string; display: string }; min?: number; max?: number; step?: number }
  | { kind: "Integer"; min?: number; max?: number; step?: number }
  | {
      kind: "Choice";
      options: readonly { id: string; label: string; detail?: string; points?: number }[];
      cardinality: "Single" | "Multiple";
      allowFreeText: boolean;
    }
  | { kind: "Boolean" }
  | { kind: "Text"; maxLength?: number; format?: "SingleLine" | "Email" | "Phone" }
  | { kind: "Date" }
  | { kind: "Attachment" }
  | { kind: "Url" };

export interface QuestionnaireQuestion {
  id: string;
  kind: "Question";
  label: string;
  help?: string;
  required: boolean;
  value: QuestionnaireAnswerValue;
  conditions?: readonly EnablementCondition[];
  conditionBehavior?: "any" | "all";
  askYesNoFirst?: boolean;
}

export interface QuestionnaireDisplay {
  id: string;
  kind: "Display";
  text: string;
}

export interface QuestionnaireRepeatingGroup {
  id: string;
  kind: "RepeatingGroup";
  label: string;
  minOccurrences: number;
  items: readonly (QuestionnaireQuestion | QuestionnaireDisplay)[];
}

export type QuestionnaireItem = QuestionnaireQuestion | QuestionnaireDisplay | QuestionnaireRepeatingGroup;

export interface QuestionnaireSection {
  id: string;
  label: string;
  /** Asked as one screen rather than one question at a time. */
  showTogether?: boolean;
  items: readonly QuestionnaireItem[];
  conditions?: readonly EnablementCondition[];
  conditionBehavior?: "any" | "all";
}

/** The high-water marks the three member namespaces have issued. */
export interface QuestionnaireMemberMarks {
  section: number;
  item: number;
  option: number;
  score: number;
}

// Deliberately not `Questionnaire/<id>`: that spelling is a FHIR reference, and
// this is a row locator in a message about a row that could not be read.
function unreadable(resource: Questionnaire, field: string, value?: unknown): never {
  throw new Error(
    `Questionnaire row ${resource.id ?? "<unknown>"} has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

function present<T>(resource: Questionnaire, field: string, value: T | undefined | null): T {
  return value === undefined || value === null ? unreadable(resource, field) : value;
}

/** Which model a row IS. Never a domain field, and never left to the caller. */
export function isQuestionnaireDefinitionRow(resource: Questionnaire): boolean {
  return resource.meta?.profile?.includes(QUESTIONNAIRE_DEFINITION_PROFILE) ?? false;
}

/**
 * The read-side twin of the mapper's guard. Refusing here is what stops an
 * exam or finding row being read as a questionnaire definition.
 */
export function assertQuestionnaireDefinitionRow(resource: Questionnaire): Questionnaire {
  if (resource.resourceType !== "Questionnaire" || !isQuestionnaireDefinitionRow(resource)) {
    unreadable(resource, "questionnaire definition profile", resource.meta?.profile);
  }
  return resource;
}

// ---------- scalars ----------

/** The lineage canonical, shared by every revision. */
export function questionnaireDefinitionUrl(resource: Questionnaire): string {
  return present(resource, "url", resource.url);
}

/** Compared and stored as an integer: a string version sorts "9" above "10". */
export function questionnaireDefinitionVersion(resource: Questionnaire): number {
  const stored = present(resource, "version", resource.version);
  const version = Number(stored);
  return Number.isFinite(version) ? version : unreadable(resource, "version", stored);
}

/** `date` is the MINT date, stamped once; `meta.lastUpdated` moves on a correction. */
export function questionnaireDefinitionMintedAt(resource: Questionnaire): string {
  return present(resource, "mintedAt", resource.date);
}

export function questionnaireDefinitionName(resource: Questionnaire): string {
  return present(resource, "name", resource.title);
}

/**
 * `status` is the storage and every non-`active` code means the lineage is not
 * offered — FHIR has four of them and the codec draws the line in exactly this
 * place. Absent is still unreadable: it is a malformed row, not "Inactive".
 */
export function questionnaireDefinitionAvailability(resource: Questionnaire): QuestionnaireDefinitionAvailability {
  return present(resource, "availability", resource.status) === "active" ? "Active" : "Inactive";
}

export function questionnaireDefinitionDescription(resource: Questionnaire): string | undefined {
  return resource.description;
}

// ---------- sections and items ----------
//
// The two domain levels ride on FHIR's arbitrary `item` recursion: a top-level
// group is a section, a nested group is a repeating group. Depth is the
// discriminator, so no marker extension is needed.

function memberId(resource: Questionnaire, what: string, linkId: string | undefined): string {
  return linkId ? linkId : unreadable(resource, `${what} identity`);
}

function bound(item: FhirQuestionnaireItem, url: string): number | undefined {
  const found = item.extension?.find((candidate) => candidate.url === url);
  return found?.valueDecimal ?? found?.valueInteger;
}

function answerValue(resource: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireAnswerValue {
  const min = bound(item, MIN_VALUE_URL);
  const max = bound(item, MAX_VALUE_URL);
  switch (item.type) {
    case "quantity": {
      const coding = item.extension?.find((candidate) => candidate.url === UNIT_URL)?.valueCoding;
      const step = bound(item, STEP_URL);
      return {
        kind: "Quantity",
        ...(coding?.code === undefined ? {} : { unit: { code: coding.code, display: coding.display ?? coding.code } }),
        ...(min === undefined ? {} : { min }),
        ...(max === undefined ? {} : { max }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "integer": {
      const step = bound(item, STEP_URL);
      return {
        kind: "Integer",
        ...(min === undefined ? {} : { min }),
        ...(max === undefined ? {} : { max }),
        ...(step === undefined ? {} : { step }),
      };
    }
    case "choice":
    case "open-choice":
      return {
        kind: "Choice",
        cardinality: item.repeats ? "Multiple" : "Single",
        allowFreeText: item.type === "open-choice",
        options: (item.answerOption ?? []).map((option) => ({
          id: memberId(resource, "choice option", option.valueCoding?.code),
          label: present(resource, "choice option.label", option.valueCoding?.display),
          ...(option.extension?.find((e) => e.url === OPTION_DETAIL_URL)?.valueString === undefined
            ? {}
            : { detail: option.extension?.find((e) => e.url === OPTION_DETAIL_URL)?.valueString }),
          ...(option.extension?.find((e) => e.url === ORDINAL_VALUE_EXTENSION_URL)?.valueDecimal === undefined
            ? {}
            : { points: option.extension?.find((e) => e.url === ORDINAL_VALUE_EXTENSION_URL)?.valueDecimal }),
        })),
      };
    case "boolean":
      return { kind: "Boolean" };
    case "date":
      return { kind: "Date" };
    case "attachment":
      return { kind: "Attachment" };
    case "url":
      return { kind: "Url" };
    // FHIR `text` is a paragraph and `string` one line; email and phone are
    // single lines named by TEXT_FORMAT_URL.
    case "text":
      return { kind: "Text", ...(item.maxLength === undefined ? {} : { maxLength: item.maxLength }) };
    case "string": {
      const code = item.extension?.find((candidate) => candidate.url === TEXT_FORMAT_URL)?.valueCode;
      return {
        kind: "Text",
        ...(item.maxLength === undefined ? {} : { maxLength: item.maxLength }),
        format: code === "email" ? "Email" : code === "phone" ? "Phone" : "SingleLine",
      };
    }
    default:
      return unreadable(resource, `question '${item.linkId}' answer kind`, item.type);
  }
}

function conditionValue(resource: Questionnaire, condition: QuestionnaireItemEnableWhen): QuestionnaireConditionValue {
  if (condition.answerBoolean !== undefined) return { kind: "Boolean", value: condition.answerBoolean };
  if (condition.answerInteger !== undefined) return { kind: "Integer", value: condition.answerInteger };
  if (condition.answerDecimal !== undefined) return { kind: "Quantity", value: condition.answerDecimal };
  if (condition.answerString !== undefined) return { kind: "Text", value: condition.answerString };
  if (condition.answerDate !== undefined) return { kind: "Date", value: condition.answerDate };
  if (condition.answerCoding?.code) return { kind: "Choice", optionId: condition.answerCoding.code };
  return unreadable(resource, `condition on '${condition.question}' comparison value`);
}

function conditions(
  resource: Questionnaire,
  enableWhen: QuestionnaireItemEnableWhen[] | undefined,
  extensions: FhirQuestionnaireItem["extension"],
): readonly EnablementCondition[] | undefined {
  const result = withFactConditions(
    (enableWhen ?? []).map(
      (condition): QuestionnaireCondition => ({
        question: memberId(resource, "condition question", condition.question),
        operator: condition.operator,
        value: conditionValue(resource, condition),
      }),
    ),
    extensions,
  );
  return result.length ? result : undefined;
}

/** Present exactly when it is "any"; "all" is FHIR's spelling of the absent domain value. */
function conditionBehavior(item: FhirQuestionnaireItem): "any" | undefined {
  return item.enableBehavior === "any" ? "any" : undefined;
}

function readItem(resource: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireItem {
  if (item.type === "display") {
    return {
      kind: "Display",
      id: memberId(resource, "display", item.linkId),
      text: present(resource, "display.text", item.text),
    };
  }
  if (item.type === "group") {
    return {
      kind: "RepeatingGroup",
      id: memberId(resource, "repeating group", item.linkId),
      label: present(resource, "repeating group.label", item.text),
      // Always written, so a group without it is malformed rather than one
      // that may be left empty.
      minOccurrences: present(
        resource,
        "repeating group.minOccurrences",
        item.extension?.find((candidate) => candidate.url === QUESTIONNAIRE_MIN_OCCURRENCES_URL)?.valueInteger,
      ),
      items: (item.item ?? []).map((child) => readItem(resource, child)) as readonly (
        | QuestionnaireQuestion
        | QuestionnaireDisplay
      )[],
    };
  }
  const help = item.item?.find(
    (child) =>
      child.type === "display" &&
      child.extension?.some(
        (e) =>
          e.url === QUESTIONNAIRE_ITEM_CONTROL_URL &&
          e.valueCodeableConcept?.coding?.some(
            (c) => c.system === QUESTIONNAIRE_ITEM_CONTROL_SYSTEM && c.code === "help",
          ),
      ),
  )?.text;
  const behavior = conditionBehavior(item);
  const enabling = conditions(resource, item.enableWhen, item.extension);
  return {
    kind: "Question",
    id: memberId(resource, "question", item.linkId),
    label: present(resource, "question.label", item.text),
    ...(help === undefined ? {} : { help }),
    required: item.required === true,
    value: answerValue(resource, item),
    ...(enabling === undefined ? {} : { conditions: enabling }),
    ...(behavior === undefined ? {} : { conditionBehavior: behavior }),
    ...(item.extension?.some((e) => e.url === ASK_YES_NO_FIRST_URL && e.valueBoolean) ? { askYesNoFirst: true } : {}),
  };
}

// SDC's `page` item control; any other control is a renderer hint nothing reads.
const PAGE_CONTROL = {
  url: QUESTIONNAIRE_ITEM_CONTROL_URL,
  valueCodeableConcept: { coding: [{ system: QUESTIONNAIRE_ITEM_CONTROL_SYSTEM, code: "page" }] },
};

function isPage(item: FhirQuestionnaireItem): boolean {
  return (
    item.extension?.some(
      (e) =>
        e.url === QUESTIONNAIRE_ITEM_CONTROL_URL &&
        e.valueCodeableConcept?.coding?.some(
          (c) => c.system === QUESTIONNAIRE_ITEM_CONTROL_SYSTEM && c.code === "page",
        ),
    ) ?? false
  );
}

function readSection(resource: Questionnaire, item: FhirQuestionnaireItem): QuestionnaireSection {
  if (item.type !== "group") unreadable(resource, `top-level item '${item.linkId}' as a section`, item.type);
  const behavior = conditionBehavior(item);
  const enabling = conditions(resource, item.enableWhen, item.extension);
  return {
    id: memberId(resource, "section", item.linkId),
    label: present(resource, "section.label", item.text),
    ...(isPage(item) ? { showTogether: true } : {}),
    ...(enabling === undefined ? {} : { conditions: enabling }),
    ...(behavior === undefined ? {} : { conditionBehavior: behavior }),
    items: (item.item ?? []).map((child) => readItem(resource, child)),
  };
}

export function questionnaireDefinitionSections(resource: Questionnaire): readonly QuestionnaireSection[] {
  return (resource.item ?? []).map((item) => readSection(resource, item));
}

// ---------- member marks ----------

function memberIds(sections: readonly QuestionnaireSection[]): {
  sections: string[];
  items: string[];
  options: string[];
} {
  const items: string[] = [];
  const options: string[] = [];
  const claim = (item: QuestionnaireItem): void => {
    items.push(item.id);
    if (item.kind === "Question" && item.value.kind === "Choice") {
      for (const option of item.value.options) options.push(option.id);
    }
    if (item.kind === "RepeatingGroup") for (const child of item.items) claim(child);
  };
  for (const section of sections) for (const item of section.items) claim(item);
  return { sections: sections.map((section) => section.id), items, options };
}

/**
 * One JSON string, and a row may carry none of it — or something that is not
 * JSON, which says the same amount about the sequences. Each missing sequence
 * is then raised to clear the members present, which is what `restore` does
 * for a lineage written before the marks were persisted: a sequence starting
 * below a live member would reissue that member's identity to a different
 * question, and every recorded answer citing the old one would repoint.
 */
export function questionnaireDefinitionMarks(
  resource: Questionnaire,
  sections: readonly QuestionnaireSection[],
): QuestionnaireMemberMarks {
  const encoded = resource.extension?.find(
    (candidate) => candidate.url === QUESTIONNAIRE_MEMBER_MARKS_URL,
  )?.valueString;
  let stored: Partial<QuestionnaireMemberMarks> = {};
  if (encoded) {
    try {
      stored = JSON.parse(encoded) as Partial<QuestionnaireMemberMarks>;
    } catch {
      stored = {};
    }
  }
  const live = memberIds(sections);
  const raise = (mark: number | undefined, ids: readonly string[]): number =>
    Math.max(mark ?? 0, highWaterMark(ids.filter(isMemberId).map((id) => ({ id }))));
  return {
    section: raise(stored.section, live.sections),
    item: raise(stored.item, live.items),
    option: raise(stored.option, live.options),
    score: raise(
      stored.score,
      questionnaireScores(resource).map((score) => score.id),
    ),
  };
}

export interface QuestionnaireScoreBand {
  label: string;
  from: number;
  to?: number;
}

export interface QuestionnaireScore {
  id: string;
  label: string;
  itemIds: readonly string[];
  bands?: readonly QuestionnaireScoreBand[];
  calculation?: QuestionnaireScoreCalculation;
}

function questionnaireScores(resource: Questionnaire): QuestionnaireScore[] {
  const stored = resource.extension?.find((e) => e.url === QUESTIONNAIRE_SCORES_URL)?.valueString;
  if (stored === undefined) return [];
  let scores: unknown;
  try {
    scores = JSON.parse(stored);
  } catch {
    return unreadable(resource, "scores");
  }
  if (!Array.isArray(scores)) return unreadable(resource, "scores");
  return scores.map((score) => {
    if (
      !score ||
      typeof score.id !== "string" ||
      typeof score.label !== "string" ||
      !Array.isArray(score.itemIds) ||
      score.itemIds.some((id: unknown) => typeof id !== "string") ||
      (score.calculation !== undefined && !isQuestionnaireScoreCalculation(score.calculation))
    )
      return unreadable(resource, "score");
    const bands = score.bands === undefined ? undefined : scoreBands(resource, score.bands);
    return {
      id: score.id,
      label: score.label,
      itemIds: score.itemIds,
      ...(bands ? { bands } : {}),
      ...(score.calculation ? { calculation: score.calculation } : {}),
    };
  });
}

function totalBands(resource: Questionnaire): QuestionnaireScoreBand[] | undefined {
  const stored = resource.extension?.find((e) => e.url === QUESTIONNAIRE_TOTAL_BANDS_URL)?.valueString;
  if (stored === undefined) return undefined;
  try {
    return scoreBands(resource, JSON.parse(stored));
  } catch {
    return unreadable(resource, "totalBands");
  }
}

function scoreBands(resource: Questionnaire, stored: unknown): QuestionnaireScoreBand[] {
  if (!Array.isArray(stored)) return unreadable(resource, "bands");
  return stored.map((band) =>
    band &&
    typeof band.label === "string" &&
    typeof band.from === "number" &&
    (band.to === undefined || typeof band.to === "number")
      ? { label: band.label, from: band.from, ...(band.to === undefined ? {} : { to: band.to }) }
      : unreadable(resource, "band"),
  );
}

// ---------- the whole row ----------

/** A stored revision, as the read side knows it. */
export interface StoredQuestionnaireDefinition {
  url: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: QuestionnaireDefinitionAvailability;
  sections: readonly QuestionnaireSection[];
  marks: QuestionnaireMemberMarks;
  scores: readonly QuestionnaireScore[];
}

/**
 * Every read at once, so that sync-time validation and the projection cannot
 * disagree about what a readable row is.
 */
export function readQuestionnaireDefinitionRow(resource: Questionnaire): StoredQuestionnaireDefinition {
  return readQuestionSetRow(assertQuestionnaireDefinitionRow(resource));
}

export function readPerformanceMeasureDefinitionRow(resource: Questionnaire): StoredQuestionnaireDefinition {
  if (resource.resourceType !== "Questionnaire" || catalogModelKindOf(resource) !== PERFORMANCE_MEASURE_DEFINITION_KIND)
    unreadable(resource, "performance measure kind", resource.meta?.tag);
  return readQuestionSetRow(resource);
}

function readQuestionSetRow(row: Questionnaire): StoredQuestionnaireDefinition {
  const description = questionnaireDefinitionDescription(row);
  const sections = questionnaireDefinitionSections(row);
  const authored = questionnaireScores(row);
  const implicit = implicitTotalScore(
    sections,
    catalogModelKindOf(row) === PERFORMANCE_MEASURE_DEFINITION_KIND,
    authored,
  );
  const bands = totalBands(row);
  const total = implicit && bands ? { ...implicit, bands } : implicit;
  return {
    url: questionnaireDefinitionUrl(row),
    version: questionnaireDefinitionVersion(row),
    mintedAt: questionnaireDefinitionMintedAt(row),
    name: questionnaireDefinitionName(row),
    ...(description === undefined ? {} : { description }),
    availability: questionnaireDefinitionAvailability(row),
    sections,
    scores: [...(total ? [total] : []), ...authored],
    marks: questionnaireDefinitionMarks(row, sections),
  };
}

// ---------- the encoders, for optimistic rows only ----------

function encodeConditions(
  conditions: readonly EnablementCondition[] | undefined,
): QuestionnaireItemEnableWhen[] | undefined {
  if (!conditions?.length) return undefined;
  const questions = conditions.filter((condition): condition is QuestionnaireCondition => !("fact" in condition));
  if (!questions.length) return undefined;
  return questions.map((condition) => ({
    question: condition.question,
    operator: condition.operator,
    // A choice condition names the OPTION IDENTITY, never its label.
    ...(condition.value.kind === "Boolean" ? { answerBoolean: condition.value.value } : {}),
    ...(condition.value.kind === "Integer" ? { answerInteger: condition.value.value } : {}),
    ...(condition.value.kind === "Quantity" ? { answerDecimal: condition.value.value } : {}),
    ...(condition.value.kind === "Text" ? { answerString: condition.value.value } : {}),
    ...(condition.value.kind === "Date" ? { answerDate: condition.value.value } : {}),
    ...(condition.value.kind === "Choice" ? { answerCoding: { code: condition.value.optionId } } : {}),
  }));
}

/** FHIR needs this whenever an item carries more than one `enableWhen`. */
function encodeBehavior(
  conditions: readonly EnablementCondition[] | undefined,
  behavior: "any" | "all" | undefined,
): "any" | "all" | undefined {
  if (!conditions?.length) return undefined;
  return behavior === "any" ? "any" : "all";
}

function encodeAnswerKind(value: QuestionnaireAnswerValue): NonNullable<FhirQuestionnaireItem["type"]> {
  switch (value.kind) {
    case "Quantity":
      return "quantity";
    case "Integer":
      return "integer";
    // `open-choice` is a FHIR spelling of `allowFreeText`, not a domain kind.
    case "Choice":
      return value.allowFreeText ? "open-choice" : "choice";
    case "Boolean":
      return "boolean";
    case "Text":
      return value.format === undefined ? "text" : "string";
    case "Date":
      return "date";
    case "Attachment":
      return "attachment";
    default:
      return "url";
  }
}

function encodeConstraints(value: QuestionnaireAnswerValue): FhirQuestionnaireItem["extension"] {
  const extensions: NonNullable<FhirQuestionnaireItem["extension"]> = [];
  if (value.kind === "Quantity") {
    if (value.unit !== undefined) {
      extensions.push({
        url: UNIT_URL,
        valueCoding: { system: UCUM_SYSTEM, code: value.unit.code, display: value.unit.display },
      });
    }
    if (value.min !== undefined) extensions.push({ url: MIN_VALUE_URL, valueDecimal: value.min });
    if (value.max !== undefined) extensions.push({ url: MAX_VALUE_URL, valueDecimal: value.max });
    if (value.step !== undefined) extensions.push({ url: STEP_URL, valueDecimal: value.step });
  }
  if (value.kind === "Integer") {
    if (value.min !== undefined) extensions.push({ url: MIN_VALUE_URL, valueInteger: value.min });
    if (value.max !== undefined) extensions.push({ url: MAX_VALUE_URL, valueInteger: value.max });
    if (value.step !== undefined) extensions.push({ url: STEP_URL, valueInteger: value.step });
  }
  if (value.kind === "Text" && (value.format === "Email" || value.format === "Phone")) {
    extensions.push({ url: TEXT_FORMAT_URL, valueCode: value.format === "Email" ? "email" : "phone" });
  }
  return extensions.length > 0 ? extensions : undefined;
}

function encodeQuestion(question: QuestionnaireQuestion): FhirQuestionnaireItem {
  const value = question.value;
  const enableWhen = encodeConditions(question.conditions);
  const behavior = encodeBehavior(question.conditions, question.conditionBehavior);
  const maxLength = value.kind === "Text" ? value.maxLength : undefined;
  const constraints = [...(encodeConstraints(value) ?? []), ...factConditionExtensions(question.conditions)];
  const extension = question.askYesNoFirst
    ? [...constraints, { url: ASK_YES_NO_FIRST_URL, valueBoolean: true }]
    : constraints.length > 0
      ? constraints
      : undefined;
  return {
    linkId: question.id,
    type: encodeAnswerKind(value),
    text: question.label,
    ...(question.help === undefined
      ? {}
      : {
          item: [
            {
              linkId: `${question.id}-help`,
              type: "display" as const,
              text: question.help,
              extension: [
                {
                  url: QUESTIONNAIRE_ITEM_CONTROL_URL,
                  valueCodeableConcept: { coding: [{ system: QUESTIONNAIRE_ITEM_CONTROL_SYSTEM, code: "help" }] },
                },
              ],
            },
          ],
        }),
    ...(question.required ? { required: true } : {}),
    ...(value.kind === "Choice" && value.cardinality === "Multiple" ? { repeats: true } : {}),
    ...(maxLength === undefined ? {} : { maxLength }),
    ...(value.kind === "Choice"
      ? {
          answerOption: value.options.map((option) => ({
            valueCoding: { code: option.id, display: option.label },
            ...(option.points === undefined && option.detail === undefined
              ? {}
              : {
                  extension: [
                    ...(option.points === undefined
                      ? []
                      : [{ url: ORDINAL_VALUE_EXTENSION_URL, valueDecimal: option.points }]),
                    ...(option.detail === undefined ? [] : [{ url: OPTION_DETAIL_URL, valueString: option.detail }]),
                  ],
                }),
          })),
        }
      : {}),
    ...(enableWhen === undefined ? {} : { enableWhen }),
    ...(behavior === undefined ? {} : { enableBehavior: behavior }),
    ...(extension === undefined ? {} : { extension }),
  };
}

function encodeItem(item: QuestionnaireItem): FhirQuestionnaireItem {
  if (item.kind === "Display") return { linkId: item.id, type: "display", text: item.text };
  if (item.kind === "RepeatingGroup") {
    return {
      linkId: item.id,
      type: "group",
      text: item.label,
      // FHIR's own spelling for "this group may be filled in more than once".
      repeats: true,
      extension: [{ url: QUESTIONNAIRE_MIN_OCCURRENCES_URL, valueInteger: item.minOccurrences }],
      item: item.items.map(encodeItem),
    };
  }
  return encodeQuestion(item);
}

export function encodeQuestionnaireSection(section: QuestionnaireSection): FhirQuestionnaireItem {
  const extension = [...(section.showTogether ? [PAGE_CONTROL] : []), ...factConditionExtensions(section.conditions)];
  const enableWhen = encodeConditions(section.conditions);
  const behavior = encodeBehavior(section.conditions, section.conditionBehavior);
  return {
    linkId: section.id,
    type: "group",
    text: section.label,
    ...(extension.length ? { extension } : {}),
    ...(enableWhen === undefined ? {} : { enableWhen }),
    ...(behavior === undefined ? {} : { enableBehavior: behavior }),
    item: section.items.map(encodeItem),
  };
}

export function encodeQuestionnaireMarks(marks: QuestionnaireMemberMarks) {
  return { url: QUESTIONNAIRE_MEMBER_MARKS_URL, valueString: JSON.stringify(marks) };
}
